import { AudioProcessorOptions, Track, TrackProcessor } from "livekit-client";
import { RNNoiseNode } from "livekit-rnnoise-processor";
import { createEffect, createRoot, createSignal, on } from "solid-js";

import { CONFIGURATION } from "@revolt/common";
import { Voice } from "@revolt/state/stores/Voice";

import { watchAudioClock } from "./audioClock";
import { createDpdfnetNode } from "./dpdfnet/node";

/**
 * Machine learning noise suppression engines
 *
 * Konkord: DPDFNet2 (max quality) and RNNoise (light). DeepFilterNet3 was
 * dropped: in our tests (VoiceBank+DEMAND and gaming noise, DNSMOS P.835) its
 * WASM build scored below RNNoise and even lowered clean speech, while costing
 * twice the CPU. DPDFNet2: OVRL 3.17 vs RNNoise 2.97, DF3 2.75, noisy 2.32.
 */
type Engine = "enhanced" | "dpdfnet";

/**
 * Konkord: RNNoise and DPDFNet2 run natively at 48 kHz, so we process at that
 * rate and let the browser do the (high quality) device resampling.
 */
const SAMPLE_RATE = 48000;

/**
 * How long a (re)started track waits for its engine before audio is passed through unprocessed
 */
const ENGINE_WAIT_MS = 1500;

/**
 * Don't retry loading DPDFNet2 for this long after a failure (or being too slow)
 */
const DPDFNET_RETRY_MS = 60_000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Konkord: noise gate (what Discord calls input sensitivity, set automatically).
 * It follows the noise floor of the already suppressed signal and closes the
 * mic a moment after speech stops, so the leftover hiss between words (raised
 * by automatic gain control) doesn't come through. Opens in 3 ms, holds for
 * 250 ms, fades out over 120 ms.
 */
const NOISE_GATE_PROCESSOR = "konkord-noise-gate";
const NOISE_GATE_CODE = `
class KonkordNoiseGate extends AudioWorkletProcessor {
  constructor() {
    super();
    this.floor = -60;
    this.gain = 0;
    this.open = false;
    this.hold = 0;
    this.enabled = true;
    this.port.onmessage = (e) => {
      if (e.data && e.data.type === "enabled") this.enabled = !!e.data.value;
    };
  }
  process(inputs, outputs) {
    const input = inputs[0];
    const output = outputs[0];
    if (!input || !input[0] || !output) return true;
    const ch = input[0];
    let sum = 0;
    for (let i = 0; i < ch.length; i++) sum += ch[i] * ch[i];
    const db = 10 * Math.log10(sum / ch.length + 1e-12);
    // the floor drops to quiet moments fast and rises very slowly (~1 min),
    // so long speech doesn't become the new floor. Exact silence (the AI
    // model resting) says nothing about the room's noise: skipped.
    if (sum > 0)
      this.floor += (db - this.floor) * (db < this.floor ? 0.05 : 0.00005);
    const openAt = Math.max(this.floor + 12, -72);
    const quantum = ch.length / sampleRate;
    if (db > openAt) {
      this.open = true;
      this.hold = 0.25;
    } else if (this.open && db < openAt - 4) {
      this.hold -= quantum;
      if (this.hold <= 0) this.open = false;
    }
    const target = !this.enabled || this.open ? 1 : 0;
    const start = this.gain;
    this.gain = target > start
      ? Math.min(1, start + quantum / 0.003)
      : Math.max(0, start - quantum / 0.12);
    for (let c = 0; c < output.length; c++) {
      const src = input[c] || ch;
      const dst = output[c];
      for (let i = 0; i < dst.length; i++) {
        dst[i] = src[i] * (start + ((this.gain - start) * i) / dst.length);
      }
    }
    return true;
  }
}
registerProcessor("konkord-noise-gate", KonkordNoiseGate);
`;

const noiseGateModules = new WeakMap<BaseAudioContext, Promise<void>>();

async function createNoiseGate(context: AudioContext) {
  let module = noiseGateModules.get(context);
  if (!module) {
    const url = URL.createObjectURL(
      new Blob([NOISE_GATE_CODE], { type: "application/javascript" }),
    );
    module = context.audioWorklet
      .addModule(url)
      .finally(() => URL.revokeObjectURL(url));
    noiseGateModules.set(context, module);
  }
  await module;
  return new AudioWorkletNode(context, NOISE_GATE_PROCESSOR);
}

let dpdfnetFailedAt = 0;

function dpdfnetUnavailable() {
  return Date.now() - dpdfnetFailedAt < DPDFNET_RETRY_MS;
}

export { preloadDpdfnet } from "./dpdfnet/node";
export { activeNoiseEngine };

/**
 * Konkord: the engine actually running on the microphone (DPDFNet2 may still be
 * loading, or have handed over to RNNoise on a slow computer), for the UI
 */
const [activeNoiseEngine, setActiveNoiseEngine] = createSignal<{
  engine?: Engine;
  loadingDpdfnet: boolean;
}>({ loadingDpdfnet: false });

export class VoiceProcessor implements TrackProcessor<
  Track.Kind.Audio,
  AudioProcessorOptions
> {
  readonly name = "stoat-voice-processor";
  processedTrack?: MediaStreamTrack;

  /**
   * Konkord: which track to send now: the processed one, or the microphone
   * untouched while the processing lags behind real time (audioClock.ts).
   * Set by whoever owns the sender.
   */
  onSend?: (track: MediaStreamTrack) => void;
  /** Konkord: the microphone as captured (muting disables this one) */
  private inputTrack?: MediaStreamTrack;
  private stopClockWatch?: () => void;

  private settings: Voice;

  /**
   * Konkord: the processor owns its audio context so it can run at 48 kHz,
   * a new one is created for every (re)started track
   */
  private audioContext?: AudioContext;
  private generation = 0;

  private sourceNode?: MediaStreamAudioSourceNode;
  private highpassNode?: BiquadFilterNode;
  private compressorNode?: DynamicsCompressorNode;
  /** Konkord: closes the mic between words, after noise suppression */
  private gateNode?: AudioWorkletNode;
  private gainNode?: GainNode;
  private destinationNode?: MediaStreamAudioDestinationNode;

  /**
   * Engine nodes are created at most once per audio context and kept around,
   * so switching modes only re-routes the graph (instant toggle, no track restart)
   */
  private engineNodes: Partial<Record<Engine, AudioNode>> = {};
  /** Konkord: cleanup for engines with more than a node (DPDFNet2's worker) */
  private engineDisposers: (() => void)[] = [];
  private loadingEngines = new Set<Engine>();
  private failedEngines = new Set<Engine>();
  private pendingLoad?: Promise<void>;

  private disposeSolidjsContext: () => void = () => {};

  constructor(voiceSettings: Voice) {
    this.settings = voiceSettings;

    // Create a solid root to track changes to the settings
    createRoot((dispose) => {
      // Konkord: noise gate on / off live
      createEffect(() => {
        this.gateNode?.port.postMessage({
          type: "enabled",
          value: this.getSettings().noiseGate,
        });
      });

      // On input volume setting change, set the gain
      createEffect(() => {
        this.setGain(this.getSettings().inputVolume);
      });

      // On noise suppression setting change, re-route the graph
      createEffect(
        on(
          () => this.getSettings().noiseSupression,
          () => this.route(),
          { defer: true },
        ),
      );

      // This is needed to destroy the solid context on unload
      this.disposeSolidjsContext = dispose;
    });
  }

  private getSettings(): Voice {
    return this.settings;
  }

  private setGain(newGain: number) {
    if (this.gainNode) {
      this.gainNode.gain.value = newGain;
    }
  }

  async init(opts: AudioProcessorOptions): Promise<void> {
    return this.build(opts.track);
  }

  async restart(opts: AudioProcessorOptions): Promise<void> {
    return this.build(opts.track);
  }

  async destroy(): Promise<void> {
    // Destroy the solid context on processor destruction
    this.disposeSolidjsContext();
    this.generation++;
    return this.teardown();
  }

  /**
   * Resolve which engine should run for the current settings, if any
   */
  private wantedEngine(): Engine | undefined {
    const mode = this.getSettings().noiseSupression;
    // Konkord: "deepfilter" (DeepFilterNet3) settings run DPDFNet2 now
    const engine: Engine | undefined =
      mode === "enhanced"
        ? "enhanced"
        : mode === "dpdfnet" || mode === "deepfilter"
          ? "dpdfnet"
          : undefined;
    if (!engine) return;

    // DPDFNet2 needs a 48 kHz context and a computer that keeps up with it,
    // otherwise fall back to RNNoise (which resamples)
    if (
      engine === "dpdfnet" &&
      (this.failedEngines.has("dpdfnet") ||
        dpdfnetUnavailable() ||
        this.audioContext?.sampleRate !== SAMPLE_RATE)
    ) {
      return this.failedEngines.has("enhanced") ? undefined : "enhanced";
    }

    return this.failedEngines.has(engine) ? undefined : engine;
  }

  /**
   * Connect the source to the output, either directly or through the active engine.
   * Everything feeding the output gain is disconnected first so raw and processed audio never mix.
   */
  private route() {
    const context = this.audioContext;
    if (!context || !this.sourceNode || !this.gainNode) return;

    this.sourceNode.disconnect();
    this.highpassNode?.disconnect();
    for (const node of Object.values(this.engineNodes)) node.disconnect();

    let engine = this.wantedEngine();
    let node = engine ? this.engineNodes[engine] : undefined;

    // Konkord: RNNoise carries the mic while DPDFNet2 downloads / starts,
    // so the voice never goes out without suppression
    if (engine === "dpdfnet" && !node) {
      void this.loadEngine("dpdfnet", context);
      if (!this.failedEngines.has("enhanced")) {
        engine = "enhanced";
        node = this.engineNodes.enhanced;
      }
    }

    setActiveNoiseEngine({
      engine: node ? engine : undefined,
      loadingDpdfnet: this.loadingEngines.has("dpdfnet"),
    });

    this.gateNode?.disconnect();

    if (engine && node) {
      this.sourceNode.connect(this.highpassNode!);
      this.highpassNode!.connect(node);
      // Konkord: what's left of the noise between words is gated away
      if (this.gateNode) {
        node.connect(this.gateNode);
        this.gateNode.connect(this.compressorNode!);
      } else {
        node.connect(this.compressorNode!);
      }
      return;
    }

    // Bypass, or pass audio through untouched while the engine loads
    this.sourceNode.connect(this.gainNode);
    if (engine) this.pendingLoad = this.loadEngine(engine, context);
  }

  private async loadEngine(engine: Engine, context: AudioContext) {
    // Sets are replaced on every build, keep a reference to the ones for this context
    const loading = this.loadingEngines;
    const failed = this.failedEngines;
    if (loading.has(engine)) return;
    loading.add(engine);

    try {
      let node: AudioNode;
      if (engine === "dpdfnet") {
        const created = await createDpdfnetNode(context, () => {
          // too slow (or crashed / stalled) here: RNNoise for a while
          if (context === this.audioContext) dpdfnetFailedAt = Date.now();
          failed.add("dpdfnet");
          if (this.engineNodes.dpdfnet === created.node) {
            delete this.engineNodes.dpdfnet;
            this.route();
          }
          // stop the worker, it's no use here
          created.dispose();
        });
        if (context !== this.audioContext) {
          created.dispose();
          return;
        }
        this.engineDisposers.push(created.dispose);
        node = created.node;
      } else {
        await RNNoiseNode.loadModule(
          context,
          CONFIGURATION.RNNOISE_WORKLET_CDN_URL,
        );
        if (context !== this.audioContext) return;
        node = new RNNoiseNode(context);
      }

      if (context !== this.audioContext) {
        node.disconnect();
        return;
      }

      // Worklet crashed at runtime, fall back to the next engine
      if (node instanceof AudioWorkletNode) {
        node.onprocessorerror = (event) => {
          console.error(`[VoiceProcessor] ${engine} crashed`, event);
          failed.add(engine);
          if (this.engineNodes[engine] === node) {
            node.disconnect();
            delete this.engineNodes[engine];
            this.route();
          }
        };
      }

      this.engineNodes[engine] = node;
    } catch (err) {
      console.error(`[VoiceProcessor] failed to load ${engine}`, err);
      failed.add(engine);
      // a context that was already replaced (device switch) says nothing
      // about the next one
      if (engine === "dpdfnet" && context === this.audioContext)
        dpdfnetFailedAt = Date.now();
    } finally {
      loading.delete(engine);
    }

    if (context === this.audioContext) this.route();
  }

  private createContext(track: MediaStreamTrack) {
    const stream = new MediaStream([track]);

    try {
      const context = new AudioContext({
        sampleRate: SAMPLE_RATE,
        latencyHint: "interactive",
      });

      try {
        return { context, source: context.createMediaStreamSource(stream) };
      } catch (err) {
        // Firefox refuses to connect a track with a different sample rate
        void context.close();
        console.warn("[VoiceProcessor] 48 kHz context unavailable", err);
      }
    } catch (err) {
      console.warn("[VoiceProcessor] 48 kHz context unavailable", err);
    }

    const context = new AudioContext({ latencyHint: "interactive" });
    return { context, source: context.createMediaStreamSource(stream) };
  }

  private async build(track: MediaStreamTrack): Promise<void> {
    const generation = ++this.generation;
    await this.teardown();
    if (generation !== this.generation) return;

    const { context, source } = this.createContext(track);
    this.audioContext = context;
    this.sourceNode = source;
    this.engineNodes = {};
    this.loadingEngines = new Set();
    this.failedEngines = new Set();
    this.pendingLoad = undefined;

    // Highpass filter in front of the engine
    this.highpassNode = context.createBiquadFilter();
    this.highpassNode.type = "highpass";
    this.highpassNode.frequency.value = 50;
    this.highpassNode.Q.value = Math.SQRT1_2;

    // Limiter after the engine
    this.compressorNode = context.createDynamicsCompressor();
    this.compressorNode.threshold.value = -3;
    this.compressorNode.knee.value = 0;
    this.compressorNode.ratio.value = 20;
    this.compressorNode.attack.value = 0.003;
    this.compressorNode.release.value = 0.05;

    // Create the target gain node for input volume
    this.gainNode = context.createGain();
    this.gainNode.gain.value = this.settings.inputVolume;
    this.compressorNode.connect(this.gainNode);

    // Create the destination node, connect the gain node and send it off to livekit
    this.destinationNode = context.createMediaStreamDestination();
    this.gainNode.connect(this.destinationNode);
    this.processedTrack = this.destinationNode.stream.getAudioTracks()[0];

    // Konkord: while this context lags behind real time the voice would break
    // up for everyone: send the microphone untouched until it catches up
    this.inputTrack = track;
    this.stopClockWatch = watchAudioClock(context, (slow) => {
      if (generation !== this.generation) return;
      console.warn(
        `[VoiceProcessor] audio ${slow ? "lagging behind real time: sending the microphone untouched" : "back to real time: processing again"}`,
      );
      const send = slow ? this.inputTrack : this.processedTrack;
      if (send) this.onSend?.(send);
    });

    // Konkord: the gate is optional, the call works without it
    const gate = await createNoiseGate(context).catch((err) => {
      console.warn("[VoiceProcessor] noise gate unavailable", err);
      return undefined;
    });
    if (generation !== this.generation || context !== this.audioContext) {
      gate?.disconnect();
      return;
    }
    if (gate) {
      gate.port.postMessage({
        type: "enabled",
        value: this.settings.noiseGate,
      });
      this.gateNode = gate;
    }

    this.route();

    // A context created outside of a user gesture may start suspended and resume() can hang
    // until the user interacts with the page, never block LiveKit's track lock on it
    await Promise.race([context.resume().catch(() => {}), sleep(200)]);
    if (context.state !== "running") {
      const resume = () => {
        if (context.state === "suspended")
          void context.resume().catch(() => {});
      };
      document.addEventListener("pointerdown", resume, { once: true });
      document.addEventListener("keydown", resume, { once: true });
    }

    // Wait briefly for the engine so the first moments of audio aren't sent unprocessed
    if (this.pendingLoad) {
      await Promise.race([this.pendingLoad, sleep(ENGINE_WAIT_MS)]);
    }
  }

  private async teardown() {
    this.stopClockWatch?.();
    this.stopClockWatch = undefined;
    this.sourceNode?.disconnect();
    this.highpassNode?.disconnect();
    for (const node of Object.values(this.engineNodes)) node.disconnect();
    this.compressorNode?.disconnect();
    this.gateNode?.disconnect();
    this.gainNode?.disconnect();
    this.destinationNode?.disconnect();

    const context = this.audioContext;
    this.audioContext = undefined;
    this.sourceNode = undefined;
    this.highpassNode = undefined;
    this.engineNodes = {};
    for (const dispose of this.engineDisposers) dispose();
    this.engineDisposers = [];
    this.compressorNode = undefined;
    this.gateNode = undefined;
    this.gainNode = undefined;
    this.destinationNode = undefined;

    await context?.close().catch(() => {});
  }
}
